export interface ServiceFAQ {
  q: string;
  a: string;
}

export interface ServicePageData {
  slug: string; // path without leading slash
  eyebrow: string;
  h1: string;
  seoTitle: string; // < 60 chars
  metaDescription: string; // < 155 chars
  intro: string;
  sections: { heading: string; body: string }[];
  bullets: string[];
  faqs: ServiceFAQ[];
}

export const servicePages: ServicePageData[] = [
  {
    slug: "monthly-bookkeeping-services",
    eyebrow: "Monthly Bookkeeping",
    h1: "Monthly Bookkeeping Services for South African Businesses",
    seoTitle: "Monthly Bookkeeping Services South Africa | From R2,500",
    metaDescription:
      "Monthly bookkeeping services for South African businesses from R2,500. SAICA registered accountants on Xero, Sage and Pastel — bank recs, VAT-ready books and management accounts.",
    intro:
      "Books that are three months behind are already costing you money — in missed VAT input claims, in overstated tax, and in decisions made from gut feel instead of numbers. Our SAICA registered team runs a disciplined monthly bookkeeping cycle on Xero, Sage or Pastel so your general ledger is closed, reconciled and reportable within the first working week of every new month.",
    sections: [
      {
        heading: "A proper month-end close, every single month",
        body:
          "Each cycle we import bank feeds from FNB, Standard Bank, ABSA, Nedbank, Capitec, TymeBank, Investec and Bidvest; process supplier bills, customer invoices, card spend, EFT clearings and cash journals; reconcile every bank, credit card, loan, petty cash and merchant account (Yoco, iKhokha, Payfast, Stripe, PayPal); and clear the control accounts — trade debtors, trade creditors, VAT, PAYE and payroll clearing. Nothing is left as 'suspense' by design. Anything unusual gets flagged in a monthly review note before the numbers are signed off.",
      },
      {
        heading: "Cloud accounting that fits how you already work",
        body:
          "We are Xero and Sage partners and comfortable in Pastel Partner, QuickBooks Online and Zoho Books. If you are still on spreadsheets or a bureau that gives you a paper trial balance once a year, we migrate you cleanly — opening balances agreed to your last set of signed annual financial statements, chart of accounts rebuilt to match how you actually run the business, and integrations wired to your point-of-sale, ecommerce platform, project software or app-store payouts.",
      },
      {
        heading: "Numbers you can act on, not just file",
        body:
          "Within the first week of the new month you receive management accounts — profit and loss compared to prior month and prior year, balance sheet, cash summary and a short commentary. VAT-registered clients get a pre-VAT201 pack showing output, input and net payable before submission. Directors of small companies get a personal drawings and loan-account view so IT3(b) surprises never happen at year-end.",
      },
      {
        heading: "One team for bookkeeping, VAT, payroll and tax",
        body:
          "Because our packages also cover VAT201, EMP201, EMP501, provisional tax, ITR14, CIPC annual returns and Beneficial Ownership, your bookkeeping feeds every downstream filing without rework, duplicate data entry or version-control fights between three different providers. One SAICA registered team, one fixed monthly fee, one point of accountability.",
      },
      {
        heading: "What it costs",
        body:
          "Basic (dormant / nil-turnover companies): from R300 per month. Entry (active businesses up to R2.3 million turnover, non-VAT): R2,500 per month. Medium (up to R12 million turnover, VAT-registered): R4,500 per month. Every tier is all-inclusive of CIPC annual return filing and SARS liaison — you never receive a surprise invoice for a routine query.",
      },
    ],
    bullets: [
      "Live bank feeds from every major SA bank",
      "Monthly bank, card and loan reconciliations",
      "Debtors, creditors and VAT control clearing",
      "Management accounts by working day 5",
      "Pre-VAT201 review pack for VAT vendors",
      "Directors' drawings and loan account tracking",
      "Xero, Sage, Pastel, QuickBooks and Zoho",
      "Integrated with payroll, tax and CIPC",
    ],
    faqs: [
      {
        q: "How much does monthly bookkeeping cost in South Africa?",
        a: "Our fixed monthly packages are R300 (dormant / nil turnover), R2,500 (Entry, up to R2.3m turnover, non-VAT) and R4,500 (Medium, up to R12m turnover, VAT-registered). Complex multi-entity groups are quoted individually. All packages are SAICA registered and all-inclusive of the CIPC annual return.",
      },
      {
        q: "How quickly can you take over my books?",
        a: "Most clients are fully onboarded in 48 hours: we get read-only access to your accounting software and bank, request opening trial balance and signed prior-year AFS, and start on the current open period. Where the books are behind we quote catch-up work separately and bring you current inside 2–4 weeks.",
      },
      {
        q: "Which accounting software do you support?",
        a: "Xero and Sage as first choice for new setups, plus Pastel Partner, QuickBooks Online and Zoho Books for existing files. We do not move you off a working system just for the sake of it — if your current setup is sound we work in it.",
      },
      {
        q: "Do I still need a bookkeeper if my accounting software 'does it all'?",
        a: "Software captures transactions; it does not decide whether an expense is a repair or a capital item, whether a bank fee is claimable VAT, or whether a payment is a loan or a drawing. Those judgements are what a SAICA registered bookkeeper adds — and what SARS looks at during a verification.",
      },
      {
        q: "Can you work with a business that is fully remote?",
        a: "Yes — we serve clients across all nine provinces without ever meeting in person. Onboarding, monthly reporting, SARS filings and queries all happen over email, WhatsApp and video calls. Documents flow through your accounting platform or a secure shared drive.",
      },
    ],
  },
  {
    slug: "payroll-emp201-emp501",
    eyebrow: "Payroll & PAYE",
    h1: "Payroll, EMP201 and EMP501 for South African Employers",
    seoTitle: "Payroll SA | EMP201 & EMP501 Submissions | SAICA",
    metaDescription:
      "Monthly payroll, EMP201, EMP501, IRP5 and IT3(a) for SA employers. SARS compliant, on-time filing, integrated with UIF and COIDA. Free quote.",
    intro:
      "PAYE is the single fastest way for a South African employer to run up SARS penalties. A missed EMP201 costs 10% on day one, an EMP501 that doesn't reconcile blocks every employee's IRP5, and directors can be held personally liable under the Fourth Schedule. We take the entire employer-tax cycle off your plate — from PAYE, UIF and SDL registration through to the bi-annual reconciliations.",
    sections: [
      {
        heading: "The monthly payroll run",
        body:
          "Every month we calculate gross pay, PAYE using the current SARS tax tables, 1% employee UIF, 1% employer UIF (capped at the prescribed ceiling), 1% SDL where turnover exceeds R500,000, medical scheme fees tax credits, retirement fund contributions, garnishee orders and any other statutory or contractual deductions. Payslips are produced in a SARS-compliant format and delivered securely to each employee. New hires, terminations, salary changes, bonuses, commission, back-pay and leave pay are handled in-cycle.",
      },
      {
        heading: "EMP201 — filed by the 7th, every month",
        body:
          "The EMP201 is the monthly employer declaration to SARS for PAYE, UIF and SDL. It is due — declared and paid — by the 7th of the following month, or the last working day before if the 7th falls on a weekend or public holiday. Late payment triggers a 10% penalty on the amount due, plus interest at the prescribed rate, on day one. We file every EMP201 on eFiling on your behalf and send you the SARS acknowledgment plus a payment reference the same day.",
      },
      {
        heading: "EMP501 interim (October) and annual (May)",
        body:
          "Twice a year SARS reconciles what you declared on EMP201 to your actual payroll and to the IRP5 / IT3(a) certificates issued to your staff. The interim EMP501 covers March–August and is due 31 October; the annual EMP501 covers the full tax year and is due 31 May. Even one cent of variance blocks certificate issuance and delays every employee's ITR12. We prepare, reconcile and submit both windows and only close them once EMP201, payroll, general ledger and certificates agree.",
      },
      {
        heading: "IRP5, IT3(a) and employee tax queries",
        body:
          "Certificates are issued through SARS e@syFile after each successful EMP501, and each employee's income shows on their eFiling profile ready to auto-populate the ITR12. When an employee raises a SARS query — 'why is code 3810 showing income I never received?' — we answer it directly, adjust and re-file where needed, and keep the audit trail.",
      },
      {
        heading: "Registration, exits and directors' remuneration",
        body:
          "If you are not yet a registered employer we handle PAYE, UIF and SDL registration on eFiling and CompEasy. Where you take on directors' remuneration under paragraph 11C of the Fourth Schedule we set the PAYE calculation up correctly from month one — avoiding the classic year-end shock where a director's provisional tax is short because PAYE was under-withheld.",
      },
    ],
    bullets: [
      "Monthly payslips and payroll journals",
      "EMP201 submission and SARS payment ref",
      "EMP501 interim and annual reconciliations",
      "IRP5 and IT3(a) certificate issuance via e@syFile",
      "PAYE, UIF and SDL employer registration",
      "Directors' remuneration under paragraph 11C",
      "Fourth Schedule compliance and audit trail",
      "SARS query handling on employee tax",
    ],
    faqs: [
      {
        q: "What is the difference between EMP201 and EMP501?",
        a: "EMP201 is a monthly declaration and payment of PAYE, UIF and SDL. EMP501 is a bi-annual reconciliation (October interim, May annual) that ties your twelve or six EMP201s to your actual payroll and to the IRP5 / IT3(a) certificates issued to employees. Both are required.",
      },
      {
        q: "What are the SARS penalties for late EMP201?",
        a: "10% administrative penalty on the amount due, applied immediately after the 7th, plus interest at the prescribed rate until settled. Continued non-compliance can escalate to criminal liability under the Fourth Schedule — payroll tax is trust money.",
      },
      {
        q: "My EMP501 will not reconcile — can you fix it?",
        a: "Yes. We routinely rebuild reconciliations that have drifted over several years, correct EMP201s where SARS allows a revised declaration, request penalty and interest remissions where the underlying cause is defensible, and re-issue IRP5s so employees can finally submit their ITR12s.",
      },
      {
        q: "Do you handle payroll for a one-person Pty (Ltd)?",
        a: "Yes — single-director companies are one of our most common payroll setups. We ensure PAYE is withheld correctly on directors' remuneration under paragraph 11C so provisional tax does not spring surprises at year-end.",
      },
      {
        q: "Which payroll software do you use?",
        a: "Xero Payroll, Sage Business Cloud Payroll and SimplePay for most clients. For larger headcounts we work in Sage 300 People. All platforms integrate directly with our monthly bookkeeping so payroll journals post automatically.",
      },
    ],
  },
  {
    slug: "uif-monthly-declarations",
    eyebrow: "UIF (Department of Labour)",
    h1: "UIF Monthly Declarations via uFiling",
    seoTitle: "UIF Monthly Declarations SA | uFiling & UI-19 Filing",
    metaDescription:
      "Monthly UIF declarations to the Department of Employment and Labour via uFiling. UI-19 lodging, employee coverage and full SARS EMP201 alignment.",
    intro:
      "Most South African employers pay UIF twice — once to SARS on the EMP201 and once to the Department of Employment and Labour on uFiling — and forget that the two declarations must agree. When they don't, employees can't claim maternity, illness, adoption or unemployment benefits when they need them most. We manage the uFiling side end-to-end and keep it in lock-step with your SARS payroll return.",
    sections: [
      {
        heading: "How UIF actually works for SA employers",
        body:
          "Every employee earning above the prescribed threshold contributes 1% of remuneration, matched by 1% from the employer, capped at the monthly ceiling gazetted under the UI Contributions Act. Contributions are declared to SARS on EMP201 and paid together with PAYE and SDL, and — separately — declared to the Department of Employment and Labour on the uFiling portal, which is what triggers the employee's benefit record at the Fund.",
      },
      {
        heading: "uFiling: new hires, terminations and UI-19",
        body:
          "Each new employee has to be added to uFiling with correct ID number, employment start date and hours worked; each termination requires a UI-19 lodgment with the correct reason code (dismissed, resigned, retrenched, contract expired, maternity, deceased) so the ex-employee can claim without the fund rejecting the application. We lodge every UI-19 the same working day the exit is processed on payroll.",
      },
      {
        heading: "Keeping SARS and Labour in agreement",
        body:
          "SARS EMP201 and Labour uFiling must report the same UIF-liable remuneration every month. Where they drift — a mid-month salary correction, a bonus that pushes an employee over the ceiling, a director whose UIF status changed — we reconcile and resubmit before either fund raises a query. This is the same reconciliation logic that later saves your EMP501 in October and May.",
      },
      {
        heading: "Domestic and small employers",
        body:
          "Domestic employers (households employing one or more workers) and small businesses under the PAYE threshold are equally obliged to register and declare with UIF. We handle the U-Filing registration, monthly declaration and annual UI-8 update — often for less than the fine a single missed declaration would attract.",
      },
    ],
    bullets: [
      "Monthly UIF declarations on uFiling",
      "Employer UI-8 and employee UI-19 lodgments",
      "New hire and termination processing",
      "Reconciliation to SARS EMP201",
      "Domestic and small-employer coverage",
      "Termination reason codes for benefit claims",
    ],
    faqs: [
      {
        q: "How is UIF calculated in South Africa?",
        a: "1% of the employee's remuneration deducted from the employee and 1% contributed by the employer, capped at the monthly ceiling published under the UI Contributions Act. Directors who receive a salary and are not the sole owner are generally UIF-liable.",
      },
      {
        q: "Why does UIF need to be declared to Labour if I already declared it on EMP201?",
        a: "The EMP201 payment goes to SARS, which forwards funds to the UIF, but only the Department of Employment and Labour uFiling declaration updates each employee's benefit record. Without the Labour declaration your employees will be told at the Fund that they have no contribution history.",
      },
      {
        q: "What happens if a UI-19 is not lodged when an employee leaves?",
        a: "The employee cannot claim unemployment, maternity or illness benefits — the Fund's online system rejects the application because there is no termination record. Employers can also be issued compliance orders under the UI Act.",
      },
      {
        q: "Do domestic employers really have to register for UIF?",
        a: "Yes — the UI Contributions Act was extended to domestic workers in 2003. Households employing even one worker are required to register and make monthly UIF declarations on uFiling.",
      },
      {
        q: "Can you handle UIF together with the rest of my payroll?",
        a: "Yes — UIF is part of every payroll package we run. It is filed at the same time as EMP201 and reconciled to the same numbers, so there is one audit trail across SARS and Labour.",
      },
    ],
  },
  {
    slug: "annual-tax-returns",
    eyebrow: "Annual Tax Returns",
    h1: "Annual Tax Returns (ITR14 & ITR12) for South African Taxpayers",
    seoTitle: "ITR14 & ITR12 Annual Tax Returns SA | SAICA CA",
    metaDescription:
      "SAICA registered filing of ITR14 company and ITR12 individual returns. SARS assessments, objections, ADR and voluntary disclosure. Free quote.",
    intro:
      "Every South African company, close corporation, trust and provisional individual taxpayer has to file an annual return with SARS — even in a loss year, even for a dormant shelf company, even where PAYE was fully withheld. We prepare and submit ITR14, ITR12 and IT12EI returns from properly reconciled numbers, defend assessments where SARS gets it wrong, and keep clients out of the escalating administrative penalty regime.",
    sections: [
      {
        heading: "ITR14 — company income tax return",
        body:
          "Every Pty (Ltd) and close corporation submits an ITR14 within twelve months of financial year-end. The return declares taxable income, calculates company tax (currently 27%), applies the small business corporation regime where the company qualifies under section 12E, treats capital gains under the Eighth Schedule, and reconciles to the signed annual financial statements. We complete the ITR14 from the AFS, tag every deduction to the source document, and submit through eFiling with a full working paper file behind it.",
      },
      {
        heading: "ITR12 — individual return",
        body:
          "Sole proprietors, directors with drawings or loan accounts, landlords, freelancers, crypto traders and taxpayers with foreign income all need an ITR12. We handle local business income, rental (with the current SARS limitation on losses ring-fenced under section 20A), interest and dividends, capital gains, foreign income and section 6quat rebates, retirement fund and medical scheme credits, and section 11(k) contribution deductions.",
      },
      {
        heading: "Trusts and public benefit organisations",
        body:
          "Discretionary and vested trusts file an IT12TR, applying the section 25B conduit principle and the current attribution rules. PBOs approved under section 30 file an IT12EI to retain their tax-exempt status. Both are areas where SARS scrutiny has tightened materially — we prepare these returns with the supporting resolutions and beneficiary distributions on file.",
      },
      {
        heading: "Assessments, objections, ADR and voluntary disclosure",
        body:
          "Once submitted, SARS issues an ITA34. We check every assessment against the return, dispute unjustified adjustments through the NOO / NOA process within the 80 business day window, and where necessary escalate to Alternative Dispute Resolution or the Tax Board. Where historical non-disclosure is the problem we structure Voluntary Disclosure Programme (VDP) applications to limit penalties and criminal exposure.",
      },
      {
        heading: "Catching up years of missed returns",
        body:
          "SARS's administrative penalty for non-submission of a personal income tax return runs from R250 to R16,000 per month per outstanding return. For companies the penalty structure was extended to ITR14 in 2024. Where clients arrive several years behind, we file everything outstanding, apply for penalty remission under section 218, and get the compliance status green again.",
      },
    ],
    bullets: [
      "ITR14 company returns from AFS",
      "ITR12 individual and sole-proprietor returns",
      "IT12TR trust returns and section 25B",
      "IT12EI for section 30 approved PBOs",
      "Section 12E small business corporation calcs",
      "Eighth Schedule capital gains treatment",
      "NOO, NOA and ADR dispute handling",
      "Voluntary Disclosure Programme applications",
    ],
    faqs: [
      {
        q: "When is my company tax return due?",
        a: "The ITR14 is due within twelve months of the company's financial year-end. For a February year-end that means the following February — but if the company is a provisional taxpayer (all companies are), the second provisional IRP6 was already due at year-end.",
      },
      {
        q: "Do I have to file if the company was dormant?",
        a: "Yes. SARS regards non-submission of a dormant company return the same way it regards non-submission of an active one — with escalating administrative penalties. A dormant ITR14 is a five-minute filing on our side and avoids the compliance flag on your eFiling profile.",
      },
      {
        q: "SARS added tax to my assessment I do not think I owe — what now?",
        a: "You have 80 business days from the assessment to lodge a Notice of Objection (NOO) with supporting documents. We handle the objection, follow up on the NOA response, and escalate to ADR or the Tax Board where SARS remains wrong.",
      },
      {
        q: "Can I still submit years of missed returns without going to jail?",
        a: "Usually yes — through the Voluntary Disclosure Programme, where the disclosure is made before SARS starts an audit. VDP typically eliminates the understatement penalty and provides certainty on the amount owing.",
      },
      {
        q: "Are you registered to act as my tax practitioner?",
        a: "Yes — as a SAICA registered practice we are recognised as controlling body tax practitioners under section 240 of the Tax Administration Act, and can appear on your eFiling profile to correspond with SARS on your behalf.",
      },
    ],
  },
  {
    slug: "provisional-tax-returns",
    eyebrow: "Provisional Tax (IRP6)",
    h1: "Provisional Tax Returns (IRP6) for SA Companies and Individuals",
    seoTitle: "Provisional Tax SA | IRP6 P1, P2 & P3 | Section 89quat",
    metaDescription:
      "IRP6 provisional tax returns for SA companies and individuals. Basic amount, under-estimation penalty, 89quat interest and cash-flow planning.",
    intro:
      "Provisional tax is where growing businesses get hurt by their own success. Under-estimate on the second provisional and SARS charges up to 20% under paragraph 20; skip the third top-up and you pay section 89quat interest at the prescribed rate compounded from year-end. We prepare defensible IRP6 estimates from live year-to-date numbers and plan the cash flow so both problems disappear.",
    sections: [
      {
        heading: "Who is actually a provisional taxpayer",
        body:
          "Every company, close corporation and trust is automatically a provisional taxpayer, regardless of size or profit. An individual is provisional if they earn taxable income from any source other than remuneration — freelance income, rental, business trading, interest above R23,800 (or R34,500 if 65+), or foreign dividends. Salaried employees whose only extra income falls below the de minimis limits are not.",
      },
      {
        heading: "P1 — the first provisional (six months in)",
        body:
          "The first IRP6 is due six months into the tax year — for a February year-end that is the last working day of August. It is calculated on the 'basic amount' (the last assessed year uplifted by 8% per year older than 18 months) unless the taxpayer legitimately estimates a lower figure. Half of the estimated annual tax is paid, less any employees' tax already withheld.",
      },
      {
        heading: "P2 — the second provisional (year-end) and the 20% trap",
        body:
          "The second IRP6 is due on the last working day of the tax year and is the estimate that matters most. If your P2 estimate falls below 80% of actual taxable income (or 90% for taxpayers with taxable income over R1 million), paragraph 20 of the Fourth Schedule imposes an under-estimation penalty of up to 20% of the shortfall in tax. We build P2 estimates from the year-to-date general ledger and a forecast for the remaining month, not from a guess.",
      },
      {
        heading: "P3 top-up — closing out section 89quat interest",
        body:
          "For February year-ends, a voluntary third payment made by 30 September stops the section 89quat interest clock. This is the difference between paying SARS interest for six to twelve months after year-end and paying nothing. For provisional taxpayers with strong final-quarter performance the P3 pays for itself many times over.",
      },
      {
        heading: "Cash flow, not just compliance",
        body:
          "Every IRP6 we file comes with a short cash-flow note: how much needs to be in the SARS payment account, when, and where the shortfall (if any) is going to come from. Provisional tax is the single biggest quarterly cash outflow most SME directors face — planning it three weeks ahead is the difference between calm and crisis.",
      },
    ],
    bullets: [
      "IRP6 first (P1) and second (P2) submissions",
      "Voluntary P3 top-up for section 89quat interest",
      "Basic amount uplift and estimate justification",
      "Paragraph 20 under-estimation penalty avoidance",
      "Employees' tax and foreign tax credit offsets",
      "Cash-flow scheduling for SARS payments",
    ],
    faqs: [
      {
        q: "How is the provisional tax basic amount calculated?",
        a: "It is the taxable income assessed on your most recent SARS assessment, uplifted by 8% for every full year the assessment is older than 18 months at the effective date of the estimate. Using the basic amount avoids the paragraph 20 under-estimation penalty on the P2, provided the assessment is not older than 14 days before the P2 due date.",
      },
      {
        q: "What is the paragraph 20 under-estimation penalty?",
        a: "If your P2 estimate is less than 80% of your final assessed taxable income (or 90% if your income exceeds R1m), SARS imposes a penalty equal to 20% of the shortfall in tax between what was paid on P2 and what would have been paid at the 80% / 90% threshold.",
      },
      {
        q: "Do I still have to file P1 and P2 in a loss year?",
        a: "Yes. Non-submission attracts administrative penalties independently of the amount owing. You file a nil IRP6 with the loss disclosed.",
      },
      {
        q: "Can I claim PAYE against my provisional tax?",
        a: "Yes. Employees' tax already withheld through payroll is deducted from the provisional tax otherwise payable. For a single-director company this is often what makes the P1 and P2 amounts nil.",
      },
      {
        q: "Is the third provisional (P3) compulsory?",
        a: "No — the P3 is voluntary, but it is the only way to stop section 89quat interest running from year-end to date of assessment. Where a shortfall on the P2 estimate is likely, the P3 usually saves money.",
      },
    ],
  },
  {
    slug: "cipc-annual-returns",
    eyebrow: "CIPC Annual Returns",
    h1: "CIPC Annual Returns for South African Companies and CCs",
    seoTitle: "CIPC Annual Returns SA | Avoid Deregistration",
    metaDescription:
      "CIPC annual returns for SA companies and close corporations. Filed on time with Beneficial Ownership. Deregistered? We restore via CoR 40.5.",
    intro:
      "Missing a CIPC annual return is the single most common reason South African companies end up deregistered — bank accounts frozen, contracts unenforceable, and property held in the company's name at risk of being declared bona vacantia and forfeited to the state. It is also the easiest problem to prevent. We track your registration anniversary, file the annual return with Beneficial Ownership every year, and restore companies that have already been struck off.",
    sections: [
      {
        heading: "What the CIPC annual return actually is",
        body:
          "It is not a tax return and it is not the AFS — it is a short annual confirmation to CIPC that the company still exists, that its directors and registered address are current, and a declaration of turnover for the fee scale. Since 2023, it must be filed together with the Beneficial Ownership register. The window is 30 business days from the anniversary of the company's incorporation date, not from the financial year-end.",
      },
      {
        heading: "The turnover-based fee scale",
        body:
          "CIPC filing fees are based on annual turnover: R100 for turnover under R1 million, R450 for R1m–R10m, R2,000 for R10m–R25m and R3,000 above R25m. Close corporation fees are lower. Late fees escalate quickly — R150 to R4,000 depending on turnover and time overdue. We calculate the correct band, pay through the CIPC customer code and pass the disbursement through at cost.",
      },
      {
        heading: "The deregistration path — and how to avoid it",
        body:
          "One year missed: CIPC status changes to 'AR Deregistration Process'. Two years missed: 'In deregistration'. After that: 'Final Deregistration' — the company is struck off the register. Banks close accounts, VAT and PAYE registrations lapse, contracts entered in the company name become unenforceable, and immovable property vests in the state. We monitor filing anniversaries thirty days ahead and file inside the grace period every year.",
      },
      {
        heading: "Restoring a deregistered company (CoR 40.5)",
        body:
          "Where the company is already deregistered, we lodge the CoR 40.5 re-instatement, gather the supporting bundle (Letter of No Objection from SARS, sworn affidavit, proof of trading or asset ownership, newspaper advert), settle every outstanding annual return and penalty, and shepherd the file through the CIPC portal. Typical timeline is six to ten weeks from lodgment to full re-instatement.",
      },
      {
        heading: "Other CIPC filings we handle in-line",
        body:
          "Change of directors (CoR 39), change of registered address (CoR 21.1), change of company name (CoR 9.1 and CoR 9.4), amendment of MOI (CoR 15.2), share capital adjustments and voluntary deregistration where a company is genuinely finished trading. All included in our Entry and Medium packages.",
      },
    ],
    bullets: [
      "Annual return filed within the 30-day window",
      "Beneficial Ownership submitted in the same run",
      "Correct turnover band and fee calculation",
      "Director, address and MOI updates",
      "CoR 40.5 re-instatement for deregistered companies",
      "SARS Letter of No Objection facilitation",
      "Close corporation annual returns",
      "Voluntary deregistration where appropriate",
    ],
    faqs: [
      {
        q: "When must a CIPC annual return be filed?",
        a: "Within 30 business days after the anniversary of the company's or close corporation's original incorporation date each year — not the financial year-end. Missing this window starts the late-fee clock immediately.",
      },
      {
        q: "How much does the CIPC annual return cost?",
        a: "R100 for turnover under R1m, R450 for R1m–R10m, R2,000 for R10m–R25m and R3,000 above R25m for companies (close corporations pay less). Our filing service is included in the monthly package; only the CIPC disbursement is passed through.",
      },
      {
        q: "Is this the same as my SARS tax return?",
        a: "No — completely separate. The CIPC annual return is a company existence confirmation; the SARS ITR14 is your income tax filing. Both are compulsory and each has its own deadline and penalty regime.",
      },
      {
        q: "What happens if the company is already deregistered?",
        a: "It cannot legally trade, contract or hold assets, and the bank will freeze the account once it picks up the deregistration on the CIPC status. We lodge CoR 40.5 with supporting documents, file every outstanding annual return and get the company re-instated — usually inside six to ten weeks.",
      },
      {
        q: "Do dormant companies still need to file?",
        a: "Yes — dormancy is not an exemption. Our Basic package (R300/month) covers the annual return and Beneficial Ownership for dormant companies and shelf entities.",
      },
    ],
  },
  {
    slug: "beneficial-ownership-submission",
    eyebrow: "Beneficial Ownership (CIPC)",
    h1: "Beneficial Ownership Register Submissions to CIPC",
    seoTitle: "Beneficial Ownership CIPC | FICA & Companies Act",
    metaDescription:
      "CIPC Beneficial Ownership register filings for SA companies and CCs. FATF grey-list, 5% threshold, bank FICA support and ongoing updates.",
    intro:
      "Beneficial Ownership disclosure is South Africa's direct response to being grey-listed by the Financial Action Task Force. Since April 2023 every company and close corporation must file a Beneficial Ownership register with CIPC, alongside the annual return and every time control changes. Non-compliance is a criminal offence under the Companies Act, and banks now refuse to onboard or maintain corporate accounts without a current BO register on file.",
    sections: [
      {
        heading: "Who counts as a beneficial owner",
        body:
          "A beneficial owner is any natural person who ultimately owns or controls 5% or more of the entity — directly, through a chain of holding companies, through a trust, through nominee shareholders or through voting arrangements. Even where the immediate shareholder is another company or a trust, CIPC wants the human at the top of the chain, with identity document, full residential address, and the mechanism by which control is exercised.",
      },
      {
        heading: "What CIPC requires in the register",
        body:
          "For each beneficial owner: full names, ID or passport number, date of birth, tax number, residential and postal address, nature and extent of beneficial interest (percentage held and whether direct or indirect), and the effective date on which they became a BO. Supporting documents — certified ID, proof of address, share registers, trust deeds, resolutions — are uploaded with the filing. 'Affected companies' (regulated by the Takeover Regulations) file the Securities Register and Disclosure of Beneficial Interest instead.",
      },
      {
        heading: "The penalties are real and personal",
        body:
          "The Companies Amendment Act 7 of 2024 gives CIPC the power to issue compliance notices, administrative fines up to 10% of turnover, and to refer criminal cases against directors personally. Beyond CIPC, every bank in South Africa now treats the BO register as a FICA due-diligence document — no current BO, no operational bank account.",
      },
      {
        heading: "Ongoing maintenance — not a one-off",
        body:
          "BO must be re-filed whenever ownership or control changes: share transfers, director appointments, trust variations, new nominee arrangements. We monitor share register events for our clients and re-file within the CIPC timeframes so the register never falls out of date between annual returns.",
      },
    ],
    bullets: [
      "Beneficial owner identification through complex structures",
      "Trust, holding company and nominee analysis",
      "Certified document collection and verification",
      "CIPC BO register filing via eServices",
      "Filed together with the annual return",
      "Ongoing updates on ownership changes",
      "Bank FICA due-diligence support pack",
      "Securities Register for affected companies",
    ],
    faqs: [
      {
        q: "Who qualifies as a beneficial owner under the Companies Act?",
        a: "Any natural person who directly or indirectly owns or exercises control over 5% or more of the entity — including through trusts, holding structures, nominee shareholdings or voting agreements. The ultimate human being always has to be identified, no matter how many corporate layers sit above them.",
      },
      {
        q: "When must the BO register be filed?",
        a: "Together with the CIPC annual return every year, and again within the prescribed timeframe whenever beneficial ownership changes. There is no exemption for dormant or small companies.",
      },
      {
        q: "What are the penalties for non-filing?",
        a: "It is a criminal offence under the Companies Act and Companies Amendment Act 7 of 2024. CIPC can issue compliance notices and administrative fines of up to 10% of turnover, and refer cases for prosecution of directors personally. Practically, your bank will also restrict the account.",
      },
      {
        q: "My company is owned by a trust — who do I disclose?",
        a: "You look through the trust to the natural persons who ultimately benefit from or control it: the founder, trustees and beneficiaries who meet the 5% control threshold. We help you work through the trust deed and letters of authority to build a defensible BO register.",
      },
      {
        q: "Do close corporations also have to file BO?",
        a: "Yes — the requirement applies to companies and close corporations equally. For CCs the beneficial owners are usually the members, but nominee arrangements and beneficial-interest agreements must still be looked through.",
      },
    ],
  },
  {
    slug: "workmens-compensation",
    eyebrow: "COIDA (Compensation Fund)",
    h1: "Workmen's Compensation (COIDA) Return of Earnings & Letter of Good Standing",
    seoTitle: "COIDA Return of Earnings SA | Letter of Good Standing",
    metaDescription:
      "COIDA registration, annual Return of Earnings on CompEasy and Letters of Good Standing for SA employers. Tender-ready, payroll-integrated.",
    intro:
      "COIDA is the compulsory workplace-injury cover that lets employees claim from the Compensation Fund instead of suing you directly. Without a current registration and a Letter of Good Standing you cannot tender for government work, cannot contract with most large corporates, and — if an employee is injured on duty — you personally carry the full civil claim. We register you, file the annual Return of Earnings on CompEasy and pull a current Letter of Good Standing whenever you need it.",
    sections: [
      {
        heading: "COIDA registration — the moment you hire one person",
        body:
          "The Compensation for Occupational Injuries and Diseases Act applies from the first employee, casual or permanent, full-time or part-time. Domestic workers were formally brought into COIDA in 2020. We register the business as an employer with the Compensation Fund, obtain the Compensation Fund reference number, and load the correct industry sub-class (which determines the assessment tariff).",
      },
      {
        heading: "The annual Return of Earnings (W.As.8) on CompEasy",
        body:
          "Every year — usually with a March–May window gazetted by the Minister — employers file the W.As.8 Return of Earnings on the CompEasy portal, declaring actual earnings for the prior assessment year and provisional earnings for the current year, capped at the annual OID earnings ceiling per employee. The Fund issues an assessment; the assessment is payable in a lump sum or (on application) in instalments.",
      },
      {
        heading: "Letter of Good Standing — the tender document",
        body:
          "A Letter of Good Standing (LoG) confirms your COIDA registration is current and your latest assessment paid. It is required by virtually every SA municipality, SOE, mine, construction principal and large corporate before you can be onboarded as a vendor or start work on site. We pull LoGs on demand — usually the same working day — and set up standing instructions where they are needed monthly.",
      },
      {
        heading: "Sub-class disputes and reassessments",
        body:
          "The Compensation Fund sometimes classifies businesses into an industry sub-class with a higher tariff than the operation warrants. Where a reclassification is defensible we lodge the objection with supporting job descriptions and payroll analysis, and get the assessment reduced going forward. Where past declarations were wrong, we file corrections through CompEasy and clear the historical balance.",
      },
      {
        heading: "Integrated with your payroll",
        body:
          "Because we already run your monthly payroll and EMP201, the earnings figures on the COIDA W.As.8 come straight from the same reconciled numbers — so the Fund never queries a variance between your declared UIF-liable pay and your COIDA-declared pay.",
      },
    ],
    bullets: [
      "Employer registration and CF reference number",
      "Correct industry sub-class classification",
      "Annual Return of Earnings on CompEasy",
      "Assessment calculation and payment planning",
      "Letters of Good Standing on demand",
      "Sub-class objections and reassessments",
      "Domestic-employer COIDA registration",
      "Payroll-tied declared earnings",
    ],
    faqs: [
      {
        q: "Who is required to register for COIDA?",
        a: "Every employer in South Africa the moment they hire a first employee — casual, part-time, permanent or domestic. There is no small-employer exemption. Directors of Pty (Ltd) companies who draw a salary are also employees for COIDA purposes.",
      },
      {
        q: "When is the Return of Earnings due?",
        a: "The submission window is gazetted each year, usually opening on 1 April and closing at the end of May or June. Late submission attracts a 10% penalty plus interest and blocks the issuance of a Letter of Good Standing.",
      },
      {
        q: "How is my COIDA assessment calculated?",
        a: "Declared earnings (capped at the annual OID earnings ceiling per employee) multiplied by the tariff for your industry sub-class. Sub-class tariffs vary enormously — an office business pays a fraction of what a construction or mining operation pays — so getting the classification right matters.",
      },
      {
        q: "How quickly can you get me a Letter of Good Standing?",
        a: "Usually the same working day, provided the latest Return of Earnings is filed and the assessment paid. Where the account is behind we clear it first — often within 48 hours — and then issue the LoG.",
      },
      {
        q: "Can I be personally sued if an employee is injured and I am not registered for COIDA?",
        a: "Yes. COIDA replaces the employee's common-law right to sue the employer with a statutory claim against the Fund. Without COIDA registration, that statutory protection falls away and the employee (or their family) can sue the employer directly for full damages.",
      },
    ],
  },
];

// Existing page reference used for the Services listing
export const existingServiceLinks = [
  {
    slug: "annual-financial-statements",
    h1: "Annual Financial Statements & CIPC Compliance",
    eyebrow: "Annual Financial Statements",
    metaDescription:
      "Annual financial statements, CIPC returns, beneficial ownership, and COIDA filings by a SAICA registered CA.",
  },
];
